import { Box, Checkbox, Flex, Radio, Stack, Tooltip } from '@mantine/core';
import { useState } from 'react';
import { useClients } from 'src/clients/hooks/useClients';
import { Client } from 'src/admin/users/types';
import { NameCell } from 'src/common/components/table/NameCell';
import { FilterHeader } from 'src/common/components/table/SearchHeader';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { formatName } from 'src/utils/nameFormatting';

export const CONTACTS_SELECTION_MODE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
} as const;

type Props = {
  selectedClients: Client[];
  onSelectClient: (client: Client) => void;
  isCheckboxDisabled?: (client: Client) => boolean;
  showAvatar?: boolean;
  showBorder?: boolean;
  selectionMode?: (typeof CONTACTS_SELECTION_MODE)[keyof typeof CONTACTS_SELECTION_MODE];
  SearchHeaderComponent?: React.ComponentType<{
    searchTerm: string;
    setSearchTerm: (term: string) => void;
  }>;
};

export function ContactsList({
  selectedClients,
  onSelectClient,
  isCheckboxDisabled = () => false,
  showAvatar = true,
  showBorder = false,
  selectionMode = CONTACTS_SELECTION_MODE.MULTIPLE,
  SearchHeaderComponent,
}: Props) {
  const { clients } = useClients();
  const [searchTerm, setSearchTerm] = useState('');
  const { client: currentClient } = useCurrentClientIfAvailable();

  const clientsWithDisabledFlag = clients
    .map(client => ({
      ...client,
      isDisabled: isCheckboxDisabled(client),
    }))
    .sort((a, b) => {
      // Move isDisabled: true to the end
      if (a.isDisabled !== b.isDisabled) {
        return a.isDisabled ? 1 : -1;
      }

      // Selected current client to top
      if (currentClient) {
        if (a.id === currentClient.id) {
          return -1;
        }
        if (b.id === currentClient.id) {
          return 1;
        }
      }

      // Sort alphabetically by lastName
      return a.lastName.localeCompare(b.lastName);
    });

  const filteredClients = clientsWithDisabledFlag.filter(client => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      client.firstName.toLowerCase().includes(searchLower) ||
      client.lastName.toLowerCase().includes(searchLower) ||
      client.emailAddress?.toLowerCase().includes(searchLower)
    );
  });

  const SelectControl = selectionMode === CONTACTS_SELECTION_MODE.MULTIPLE ? Checkbox : Radio;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      {SearchHeaderComponent ? (
        <SearchHeaderComponent searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      ) : (
        <FilterHeader
          direction="row-reverse"
          mb="md"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          inputProps={{
            maw: undefined,
          }}
          placeholder={'Search client'}
        />
      )}
      <Stack
        gap="xs"
        p={showBorder ? 'md' : undefined}
        bd={showBorder ? '1px solid var(--mantine-color-gray-1)' : undefined}
        bg={showBorder ? 'white' : undefined}
        style={{
          overflowY: 'scroll',
          scrollbarWidth: 'thin',
          borderRadius: showBorder ? 12 : undefined,
        }}
      >
        {filteredClients.map(client => (
          <Tooltip
            key={client.id}
            label="This client is already in another family"
            disabled={!client.isDisabled}
          >
            <Flex
              align="center"
              gap="xs"
              onClick={() => {
                if (!client.isDisabled) onSelectClient(client);
              }}
              style={{
                cursor: client.isDisabled ? 'not-allowed' : 'default',
                opacity: client.isDisabled ? 0.5 : 1,
              }}
              role={'listitem'}
            >
              <SelectControl
                checked={selectedClients.some(c => c.id === client.id)}
                aria-label={`Select ${formatName(client.firstName, client.lastName)}`}
                onChange={() => {}}
                disabled={client.isDisabled}
              />
              <NameCell entity={client} showAvatar={showAvatar} />
            </Flex>
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );
}
